/**
 * シーン単位のフレーム生成設定 (追加プロンプト・参照画像の抜き差し) の保存と読み出し。
 * docs/features/scene-frame-generation-tuning-plan.md §6.7。
 *
 * 保存の順序付けは SceneFrameSettingsQueue (シーン単位の直列化・flush・reset)。
 * 保存結果は lastResult のシーン行へ反映し、画面はそこから読む (正本 = 保存済みの行)。
 */
import { useCallback, useMemo, useRef } from 'react';
import type { UpdateScenePayload } from '../../../../features/scenarios/types';
import type { ScenarioResponse } from '../types';
import {
  SceneFrameSettingsQueue,
  sceneFrameSettingsFromScene,
  type SceneFrameSettings,
  type SceneFrameSettingsPatch,
  type SceneFrameSettingsUpdate,
} from './sceneFrameSettingsQueue';

type SceneLike = Parameters<typeof sceneFrameSettingsFromScene>[0] & { id: string };

interface UseSceneFrameSettingsParams {
  updateScene: (args: { sceneId: string; payload: UpdateScenePayload }) => Promise<unknown>;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
}

export interface SceneFrameSettingsApi {
  /** 差分を保存する (この差分が保存されるまで待つ。失敗なら投げる)。
   *  関数を渡すと意図した最新値 (保存済み + 送信中 + 未送信) に対して差分を作る (連続操作で上書きしない) */
  saveSceneFrameSettings: (scene: SceneLike, update: SceneFrameSettingsUpdate) => Promise<SceneFrameSettings>;
  /** 保留中・送信中の保存を待ち、保存済みの最新設定を返す。生成要求はこの戻り値から組む */
  flushSceneFrameSettings: (scene: SceneLike) => Promise<SceneFrameSettings>;
  /** 未送信を破棄し、送信中の完了を待ってから未設定を保存する */
  resetSceneFrameSettings: (scene: SceneLike) => Promise<SceneFrameSettings>;
  /** 失敗した保存を送り直す */
  retrySceneFrameSettings: (scene: SceneLike) => Promise<SceneFrameSettings>;
  /** 保存済みの最新設定 (未登録なら行の値) */
  readSceneFrameSettings: (scene: SceneLike) => SceneFrameSettings;
  /** 保存済み・未送信の差分・保存失敗をまとめて読む (ダイアログの再表示で未保存の入力と再試行を復元する) */
  peekSceneFrameSettings: (scene: SceneLike) => { saved: SceneFrameSettings; pending: SceneFrameSettingsPatch | null; error: unknown };
  /** 未送信の差分と保存失敗を破棄する */
  discardSceneFrameSettings: (scene: SceneLike) => void;
}

export function useSceneFrameSettings({ updateScene, setLastResult }: UseSceneFrameSettingsParams): SceneFrameSettingsApi {
  const updateSceneRef = useRef(updateScene);
  updateSceneRef.current = updateScene;
  const setLastResultRef = useRef(setLastResult);
  setLastResultRef.current = setLastResult;

  const queue = useMemo(() => new SceneFrameSettingsQueue(async (sceneId, patch) => {
    const payload: UpdateScenePayload = {};
    if ('additionalPrompt' in patch) payload.frame_additional_prompt = patch.additionalPrompt || null;
    if ('overrides' in patch) payload.frame_reference_overrides = patch.overrides ?? null;
    await updateSceneRef.current({ sceneId, payload });
    // 保存済みの値を画面の行へ反映する (テロップ設定と同じ流儀)
    setLastResultRef.current((prev: ScenarioResponse | null) => (prev ? {
      ...prev,
      scenarios: prev.scenarios.map((s: any) => ({
        ...s,
        scenes: s.scenes.map((sc: any) => (sc.id === sceneId ? { ...sc, ...payload } : sc)),
      })),
    } : prev));
  }), []);

  const seed = useCallback((scene: SceneLike) => {
    queue.seedIfAbsent(scene.id, sceneFrameSettingsFromScene(scene));
  }, [queue]);

  const saveSceneFrameSettings = useCallback((scene: SceneLike, update: SceneFrameSettingsUpdate) => {
    seed(scene);
    return queue.save(scene.id, update);
  }, [queue, seed]);
  const flushSceneFrameSettings = useCallback((scene: SceneLike) => {
    seed(scene);
    return queue.flush(scene.id);
  }, [queue, seed]);
  const resetSceneFrameSettings = useCallback((scene: SceneLike) => {
    seed(scene);
    return queue.reset(scene.id);
  }, [queue, seed]);
  const retrySceneFrameSettings = useCallback((scene: SceneLike) => {
    seed(scene);
    return queue.retry(scene.id);
  }, [queue, seed]);
  const readSceneFrameSettings = useCallback((scene: SceneLike) => {
    seed(scene);
    return queue.get(scene.id);
  }, [queue, seed]);
  const peekSceneFrameSettings = useCallback((scene: SceneLike) => {
    seed(scene);
    return { saved: queue.get(scene.id), pending: queue.getPending(scene.id), error: queue.getError(scene.id) };
  }, [queue, seed]);
  const discardSceneFrameSettings = useCallback((scene: SceneLike) => {
    queue.discardPending(scene.id);
  }, [queue]);

  return useMemo(() => ({
    saveSceneFrameSettings,
    flushSceneFrameSettings,
    resetSceneFrameSettings,
    retrySceneFrameSettings,
    readSceneFrameSettings,
    peekSceneFrameSettings,
    discardSceneFrameSettings,
  }), [saveSceneFrameSettings, flushSceneFrameSettings, resetSceneFrameSettings, retrySceneFrameSettings, readSceneFrameSettings, peekSceneFrameSettings, discardSceneFrameSettings]);
}
